import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

/**
 * T2TSapPushPanel \u2014 Shows SAP push status for a vendor request.
 * Displays on Request Detail page after workflow reaches SAP Push stage.
 *
 * Props:
 *  - requestId: string/number
 *  - vendorId: string/number (if vendor already created)
 *  - sapVendorNumber: string (returned by SAP or MOCK-VND-...)
 */
const T2TSapPushPanel = ({ requestId, vendorId, sapVendorNumber }) => {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    const fetchLogs = async () => {
      setLoading(true);
      const resp = await getData(`t2t/requests/${requestId}/sap-log`);
      if (!resp.error) {
        const raw = resp.data?.data;
        setLogs(Array.isArray(raw) ? raw : (raw?.logs || []));
      }
      setLoading(false);
    };
    fetchLogs();
  }, [requestId]);

  const handleRetry = async () => {
    setRetrying(true);
    const endpoint = vendorId ? `t2t/vendors/${vendorId}/sap-retry` : `t2t/requests/${requestId}/sap-retry`;
    const resp = await postData(endpoint, {});
    if (resp.error) {
      toast(resp.error.message || "SAP retry failed.", "error");
    } else {
      toast("SAP push retried. Check status in a moment.", "success");
      // Refresh logs
      const logResp = await getData(`t2t/requests/${requestId}/sap-log`);
      if (!logResp.error) {
        const raw = logResp.data?.data;
        setLogs(Array.isArray(raw) ? raw : (raw?.logs || []));
      }
    }
    setRetrying(false);
  };

  const isMock = sapVendorNumber && sapVendorNumber.startsWith("MOCK");
  const latestLog = logs.length > 0 ? logs[0] : null;
  const isSuccess = latestLog?.status === "success" || latestLog?.status === "ok";
  const isFailed = latestLog?.status === "failed" || latestLog?.status === "error";
  const isPending = !latestLog || latestLog?.status === "pending" || latestLog?.status === "processing";

  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: isSuccess ? "#F0FDF4" : isFailed ? "#FEF2F2" : "#F8FAFC",
        borderBottom: "1px solid #E2E8F0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>
            {isSuccess ? "\u2705" : isFailed ? "\u274C" : isPending ? "\u23F3" : "\uD83D\uDD04"}
          </span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>SAP Integration — BAPI_VENDOR_CREATE</div>
            <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginTop: 2 }}>
              {isSuccess && "Vendor successfully created in SAP"}
              {isFailed && "SAP push failed \u2014 see error details below"}
              {isPending && "SAP push pending or in progress"}
              {!latestLog && loading && "Loading SAP status..."}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isMock && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#FFF7ED", color: "#D97706", border: "1px solid #FED7AA" }}>
              MOCK MODE
            </span>
          )}
          {(isFailed || isMock) && (
            <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={handleRetry} disabled={retrying} style={{ fontSize: 11 }}>
              {retrying ? "Retrying..." : "\uD83D\uDD04 Retry SAP Push"}
            </button>
          )}
        </div>
      </div>

      {/* SAP Vendor Number */}
      {sapVendorNumber && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>SAP Vendor Number:</div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: isMock ? "#D97706" : "#16A34A" }}>
            {sapVendorNumber}
          </div>
          {isMock && (
            <div style={{ fontSize: 10, color: "#D97706" }}>
              Mock number \u2014 will be replaced when real SAP credentials are configured
            </div>
          )}
        </div>
      )}

      {/* Push Log */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t2t-text-muted)", marginBottom: 8 }}>Push History ({logs.length} attempt{logs.length !== 1 ? "s" : ""})</div>

        {loading && <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading logs...</div>}

        {!loading && logs.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No SAP push attempts recorded yet.</div>
        )}

        {logs.map((log, i) => {
          const logSuccess = log.status === "success" || log.status === "ok";
          const logFailed = log.status === "failed" || log.status === "error";
          const isExp = expanded === i;

          return (
            <div key={log.id || i} style={{ borderBottom: i < logs.length - 1 ? "1px solid #F1F5F9" : "none", padding: "8px 0" }}>
              <div
                onClick={() => setExpanded(isExp ? null : i)}
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
              >
                <span style={{ fontSize: 14 }}>
                  {logSuccess ? "\uD83D\uDFE2" : logFailed ? "\uD83D\uDD34" : "\uD83D\uDFE1"}
                </span>
                <div style={{ flex: 1, fontSize: 12 }}>
                  <span style={{ fontWeight: 600 }}>Attempt {logs.length - i}</span>
                  <span style={{ color: "var(--t2t-text-muted)", marginLeft: 8 }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString() : ""}
                  </span>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                  background: logSuccess ? "#F0FDF4" : logFailed ? "#FEF2F2" : "#FFF7ED",
                  color: logSuccess ? "#16A34A" : logFailed ? "#DC2626" : "#D97706",
                }}>
                  {log.status?.toUpperCase() || "PENDING"}
                </span>
                <span style={{ fontSize: 10, color: "#94A3B8" }}>{isExp ? "\u25B2" : "\u25BC"}</span>
              </div>

              {isExp && (
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#F8FAFC", borderRadius: 6, fontSize: 11 }}>
                  {log.sap_vendor_number && (
                    <div style={{ marginBottom: 4 }}><strong>SAP Vendor #:</strong> <span style={{ fontFamily: "monospace" }}>{log.sap_vendor_number}</span></div>
                  )}
                  {log.error_message && (
                    <div style={{ marginBottom: 4, color: "#DC2626" }}><strong>Error:</strong> {log.error_message}</div>
                  )}
                  {log.error_code && (
                    <div style={{ marginBottom: 4 }}><strong>Error Code:</strong> {log.error_code}</div>
                  )}
                  {log.response_ms && (
                    <div style={{ marginBottom: 4 }}><strong>Response Time:</strong> {log.response_ms}ms</div>
                  )}
                  {log.bapi_name && (
                    <div style={{ marginBottom: 4 }}><strong>BAPI:</strong> {log.bapi_name}</div>
                  )}
                  {log.payload && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: "pointer", fontWeight: 600, color: "var(--t2t-primary)" }}>View Payload</summary>
                      <pre style={{ fontSize: 10, background: "#1B3A6B", color: "#E2E8F0", padding: 10, borderRadius: 4, marginTop: 4, overflowX: "auto", maxHeight: 200 }}>
                        {typeof log.payload === "string" ? log.payload : JSON.stringify(log.payload, null, 2)}
                      </pre>
                    </details>
                  )}
                  {log.response && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: "pointer", fontWeight: 600, color: "var(--t2t-primary)" }}>View Response</summary>
                      <pre style={{ fontSize: 10, background: "#1B3A6B", color: "#E2E8F0", padding: 10, borderRadius: 4, marginTop: 4, overflowX: "auto", maxHeight: 200 }}>
                        {typeof log.response === "string" ? log.response : JSON.stringify(log.response, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

T2TSapPushPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sapVendorNumber: PropTypes.string,
};

export default T2TSapPushPanel;
